import { Layout } from "antd";
import { type BasicProps } from "antd/lib/layout/layout";
import {
    type ContextType,
    type FC,
    PureComponent,
    type ReactNode,
    useCallback,
    useState,
} from "react";
import { useInView } from "react-intersection-observer";

import { cn } from "@btnet/utilities-core";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

import { useResizeObserver } from "utilities/resizeObserver/useResizeObserver";

import { FocusProvider } from "commonComponents/utilities/Focus/FocusProvider";
import { useStickyHeight } from "commonComponents/utilities/StickyContext/StickyContext";

import "./BTLayout.less";

interface IBTLayoutProps {
    shouldExpandToFitContent?: boolean;
    /**
     * when true, removes a webforms css hack that adds content to the end of BTLayoutHeader
     * @default false
     */
    removeHeaderAfter?: boolean;
    useModalLayout?: boolean;
    removePadding?: boolean;
    children: ReactNode;
}

export const BTLayout: FC<IBTLayoutProps> = ({
    shouldExpandToFitContent = false,
    removeHeaderAfter = false,
    ...props
}) => {
    if (props.useModalLayout) {
        return <>{props.children}</>;
    }
    const layoutClasses = cn({
        layout: true,
        "BTLayout-ExpandToFillContent": shouldExpandToFitContent,
        "BTLayout-RemoveHeaderAfter": removeHeaderAfter,
    });

    return (
        // eslint-disable-next-line react/forbid-elements
        <Layout className={layoutClasses}>
            <FocusProvider shareFocusWithParent>{props.children}</FocusProvider>
        </Layout>
    );
};

interface IBTLayoutHeaderProps extends BasicProps {
    /**
     * Indicates if the Layout Header should stick to the bottom of the current
     * StickyHeaderContext stack. Provide the height of the Layout Header if
     * nested elements also need to be added to the StickyHeaderContext stack.
     *
     * @default true
     */
    isSticky?: boolean;

    /**
     * Only set this to `true` when using BTModalLayout in a presentational  component!
     * If this is set to `true`, this component will not return anything
     *
     * @default false
     */
    useModalLayout?: boolean;
    children?: ReactNode;
}

/**
 * BTModal header layout
 * @example
 * <BTLayout>
 *  <BTButton type="primary" data-testid="save">Save</BTButton>
 *  <BTButton type="destruction" data-testid="delete">Delete</BTButton>
 * </BTLayout>
 */
export const BTLayoutHeader: FC<IBTLayoutHeaderProps> = ({
    isSticky = true,
    className,
    children,
    useModalLayout = false,
    style,
    ...restProps
}) => {
    /**
     * Note: this can be replaced with the "sticky-change" event if it becomes a standard.
     *
     * .TopScrollPlaceholder is tracked, so when it's no longer visible we know the user is not
     * at the top of the window anymore. This triggers a rerender and we add the .stuck class.
     */
    const { ref, inView } = useInView({
        threshold: 1,
    });

    const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
    const [, setHeaderRef] = useResizeObserver((entry) => {
        const headerRef = entry?.target;
        if (isSticky && headerRef) {
            const defaultHeaderHeight = 48;
            setStickyHeaderHeight(headerRef.clientHeight ?? defaultHeaderHeight);
        }
    });
    const top = useStickyHeight(stickyHeaderHeight, "top", 0);

    const setRefs = useCallback(
        (r: HTMLDivElement | null) => {
            ref(r);
            // TODO: remove this hack and set ref directly to header ref once we are on antd v4.19.4+ (https://github.com/ant-design/ant-design/commit/8cab1883afeea049fa8554fe70d0491830d95b78#diff-f22153e3f63b1ae5f7b6d84d7ecbd52b795c4ccd564a452021da1205d7c17658)
            setHeaderRef((r?.nextElementSibling ?? null) as HTMLElement | null);
        },
        [ref, setHeaderRef],
    );

    if (useModalLayout) {
        return null;
    }

    return (
        <>
            <div ref={setRefs} className="TopScrollPlaceholder" />
            {children && (
                <Layout.Header
                    {...restProps}
                    className={cn(
                        "ModalHeader",
                        {
                            StickyLayoutHeader: isSticky,
                            stuck: isSticky && !inView,
                        },
                        className,
                        "BTLayoutHeader",
                    )}
                    style={{
                        ...style,
                        ...(isSticky && { top }),
                    }}
                >
                    {children}
                </Layout.Header>
            )}
        </>
    );
};

export class BTLayoutContent extends PureComponent<{
    removePadding?: boolean;
    children: ReactNode;
}> {
    static contextType = BuilderInfoContext;
    declare context: ContextType<typeof BuilderInfoContext>;

    render() {
        return (
            <Layout.Content
                className={cn("BTLayoutContent", {
                    removePadding: this.props.removePadding,
                })}
            >
                {this.props.children}
            </Layout.Content>
        );
    }
}

export class BTLayoutFooter extends PureComponent<{ children: ReactNode }> {
    render() {
        return <Layout.Footer className="BTLayoutFooter">{this.props.children}</Layout.Footer>;
    }
}
